import { defineMessages } from "react-intl"
import { BulkActionDefinition, BulkActionType, ColumnDefinition, ColumnDefinitionCategory, DataType, TotalType } from "."
import { ObjectType, ObjectTypeConfig } from ".."
import { Permissions } from "../../../../role/Guard"
import { BULK_OPERATION_MESSAGES, BULK_OPERATION_TYPE, isQuoteBulkExportSupported } from "../../../../utils/automation/bulk"
import { PREFERENCE_TYPE } from "../../constants"

export enum QuoteColumnIds {
    IDENTIFIER = "identifier",
    LOCATION_NAME = "locationName",
    PROJECT_TITLE = "projectTitle",
    CONTACT = "contact",
    PROPOSED_AT_DATE = "proposedAt",
    CREATED_AT_DATE = "createdAt",
    STATUS = "status",
    TOTAL_VALUE = "totalValue",
    ARCHIVED = "isArchived",
    PO_NUMBER = "poNumber",
    CUSTOMER_TYPE = "customerType",
    DECLINED_REASON = "declinedReason",
    DECLINED_REASON_NOTES = "declinedReasonNotes",
    DRAFT_AT_DATE = "draftAt",
    CANCELLED_AT_DATE = "cancelledAt",
    PRE_APPROVED_AT_DATE = "preApprovedAt",
    BALANCE = "balance",
    TOTAL_AMOUNT = "totalAmount",
    UPDATED_AT_DATE = "updatedAt",
    APPROVED_AT_DATE = "approvedAt",
    DECLINED_AT_DATE = "declinedAt",
    PRE_DECLINED_AT_DATE = "preDeclinedAt",
    INVOICED_AT_DATE = "invoicedAt",
    DEPOSIT_PAID_AT_DATE = "depositPaidAt",
    EXPIRATION_DATE = "expirationDate",
    ORDERED_AT_DATE = "orderedAt",
    CONTACT_NAME = "contactName",
    CONTACT_FIRST_NAME = "contactFirstName",
    CONTACT_LAST_NAME = "contactLastName",
    CONTACT_ORGANIZATION_NAME = "contactOrganizationName",
    CONTACT_TYPE = "contactType",
    CONTACT_PHONE_NUMBER = "contactPhoneNumber",
    CONTACT_TAX_NUMBER = "contactTaxNumber",
    CONTACT_EMAIL = "contactEmail",
    PAID_AMOUNT = "paidAmount",
    CURRENCY = "currency",
    TAX_ID = "taxId",
    PAYMENT_TRANSACTIONS_TYPE = "paymentTransactionsType",
    PAYMENT_TRANSACTIONS_AMOUNT = "paymentTransactionsAmount",
    PRODUCTS_TITLE = "productsTitle",
    PRODUCTS_SKU = "productsSku",
    PRODUCTS_LABELS = "productsLabels",
    TAX_AMOUNT = "totalTaxAmount"
}

export enum QuoteStatBox {
    DRAFT = "draft",
    PROPOSED = "proposed",
    ACCEPTED = "accepted",
    WON = "won",
}

const columnDefinitions: ColumnDefinition[] = [
    {
        id: QuoteColumnIds.IDENTIFIER,
        sortId: "identifierInteger",
        defaultSort: true,
        dataType: DataType.LINK,
        dataAttributes: {
            redirect: "/quotes/{id}/edit",
            secondaryValueKey: QuoteColumnIds.PO_NUMBER,
        },
        totalAttributes: {
            key: "totalItemsCount",
            type: TotalType.STRING,
        },
        sortable: true,
        identifier: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: QuoteColumnIds.LOCATION_NAME,
        sortId: "locationNameSort",
        dataType: DataType.LOCATION,
        dataAttributes: {
            redirect: "/locations/{locationId}/edit"
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: QuoteColumnIds.PROJECT_TITLE,
        sortId: "projectTitleSort",
        dataType: DataType.STRING,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: QuoteColumnIds.CONTACT,
        accessorFn: row => row.contact?.name,
        sortId: "contact.nameSort",
        dataType: DataType.CONTACT,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: QuoteColumnIds.PROPOSED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: QuoteColumnIds.CREATED_AT_DATE,
        dataType: DataType.DATE,
        dataAttributes: {
            textAlign: "right",
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: QuoteColumnIds.STATUS,
        dataType: DataType.BADGE,
        dataAttributes: {
            badgeConfigMap: {
                "new": {
                    message: {
                        id: "generic-listing.quotes.badge.new",
                        defaultMessage: "New",
                    },
                    type: "primary",
                },
                "draft": {
                    message: {
                        id: "generic-listing.quotes.badge.draft",
                        defaultMessage: "Draft",
                    },
                    type: "primary",
                },
                "cancelled": {
                    message: {
                        id: "generic-listing.quotes.badge.cancelled",
                        defaultMessage: "Cancelled",
                    },
                    type: "light",
                },
                "proposed": {
                    message: {
                        id: "generic-listing.quotes.badge.proposed",
                        defaultMessage: "Proposed",
                    },
                    type: "info",
                },
                "pre-approved": {
                    message: {
                        id: "generic-listing.quotes.badge.pre-approved",
                        defaultMessage: "Pre-approved",
                    },
                    type: "warning",
                },
                "pre-declined": {
                    message: {
                        id: "generic-listing.quotes.badge.pre-declined",
                        defaultMessage: "Pre-declined",
                    },
                    type: "warning",
                },
                "approved": {
                    message: {
                        id: "generic-listing.quotes.badge.approved",
                        defaultMessage: "Won",
                    },
                    type: "success",
                },
                "declined": {
                    message: {
                        id: "generic-listing.quotes.badge.declined",
                        defaultMessage: "Lost",
                    },
                    type: "danger",
                },
                "work-in-progress": {
                    message: {
                        id: "generic-listing.quotes.badge.work-in-progress",
                        defaultMessage: "Work in progress",
                    },
                    type: "warning",
                },
                "work-complete": {
                    message: {
                        id: "generic-listing.quotes.badge.work-completed",
                        defaultMessage: "Work completed",
                    },
                    type: "success",
                },
                "invoiced": {
                    message: {
                        id: "generic-listing.quotes.badge.invoiced",
                        defaultMessage: "Invoiced",
                    },
                    type: "custom-secondary",
                },
                "ordered": {
                    message: {
                        id: "generic-listing.quotes.badge.ordered",
                        defaultMessage: "Ordered",
                    },
                    type: "custom-secondary",
                },
            },
            textAlign: "right",
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: QuoteColumnIds.TOTAL_VALUE,
        accessorFn: row => row.totalValue ?? row.totalAmount,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            showPaidAmount: true,
            paidAmountKey: "paidAmount",
            textAlign: "right",
        },
        totalAttributes: {
            key: "totals.baseCurrencyTotalValueAmount",
            type: TotalType.CURRENCY,
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: QuoteColumnIds.ARCHIVED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: QuoteColumnIds.PO_NUMBER,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: QuoteColumnIds.CUSTOMER_TYPE,
        sortId: "contact.customerTypeName",
        accessorFn: row => row.contact?.customerTypeName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: QuoteColumnIds.DECLINED_REASON,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: QuoteColumnIds.DECLINED_REASON_NOTES,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: QuoteColumnIds.DRAFT_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: QuoteColumnIds.CANCELLED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: QuoteColumnIds.PRE_APPROVED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: QuoteColumnIds.TOTAL_AMOUNT,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: QuoteColumnIds.BALANCE,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: QuoteColumnIds.UPDATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: QuoteColumnIds.APPROVED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: QuoteColumnIds.DECLINED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: QuoteColumnIds.PRE_DECLINED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: QuoteColumnIds.INVOICED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: QuoteColumnIds.DEPOSIT_PAID_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: QuoteColumnIds.EXPIRATION_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: QuoteColumnIds.ORDERED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: QuoteColumnIds.CONTACT_NAME,
        sortId: "contact.nameSort",
        accessorFn: row => row.contact?.name,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: QuoteColumnIds.CONTACT_FIRST_NAME,
        sortId: "contact.firstNameKeyword",
        accessorFn: row => row.contact?.firstName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: QuoteColumnIds.CONTACT_LAST_NAME,
        sortId: "contact.lastNameKeyword",
        accessorFn: row => row.contact?.lastName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: QuoteColumnIds.CONTACT_ORGANIZATION_NAME,
        sortId: "contact.organizationNameSort",
        accessorFn: row => row.contact?.organizationName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: QuoteColumnIds.CONTACT_TYPE,
        accessorFn: row => row.contact?.type,
        dataType: DataType.STRING,
        dataAttributes: {
            messageMap: {
                "persons": {
                    id: "generic-listing.contact-type.people",
                    defaultMessage: "People",
                },
                "organizations": {
                    id: "generic-listing.contact-type.organization",
                    defaultMessage: "Organization",
                },
                "suppliers": {
                    id: "generic-listing.contact-type.supplier",
                    defaultMessage: "Supplier",
                },
            }
        },
        sortable: false,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: QuoteColumnIds.CONTACT_PHONE_NUMBER,
        sortId: "contact.phoneNumberKeyword",
        accessorFn: row => row.contact?.phoneNumber,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: QuoteColumnIds.CONTACT_TAX_NUMBER,
        sortId: "contact.taxNumber",
        accessorFn: row => row.contact?.taxNumber,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: QuoteColumnIds.CONTACT_EMAIL,
        sortId: "contact.emailKeyword",
        accessorFn: row => row.contact?.email,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: QuoteColumnIds.PAID_AMOUNT,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: QuoteColumnIds.CURRENCY,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: QuoteColumnIds.TAX_AMOUNT,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: QuoteColumnIds.TAX_ID,
        dataType: DataType.OPTIONS,
        dataAttributes: {
            optionsKey: "taxOptions",
            loadingKey: "loadingTaxOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: QuoteColumnIds.PAYMENT_TRANSACTIONS_TYPE,
        accessorKey: "paymentTransactions",
        sortId: "paymentTransactionsObject.paymentType",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "paymentTypeName",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: QuoteColumnIds.PAYMENT_TRANSACTIONS_AMOUNT,
        accessorKey: "paymentTransactions",
        sortId: "paymentTransactionsObject.amount",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "amount",
            textAlign: "right",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: QuoteColumnIds.PRODUCTS_TITLE,
        accessorKey: "rows",
        sortId: "rowsObject.title",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "title",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: QuoteColumnIds.PRODUCTS_SKU,
        accessorKey: "rows",
        sortId: "rowsObject.sku",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "sku",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: QuoteColumnIds.PRODUCTS_LABELS,
        accessorKey: "rows",
        sortId: "rowsObject.labelIds",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "labelIds",
            optionsKey: "labelOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
]

const headerMessages = defineMessages({
    [QuoteColumnIds.IDENTIFIER]: {
        id: "generic-listing.quotes.table-header.identifier",
        defaultMessage: "ID",
    },
    [QuoteColumnIds.LOCATION_NAME]: {
        id: "generic-listing.quotes.table-header.locationName",
        defaultMessage: "Location",
    },
    [QuoteColumnIds.PROJECT_TITLE]: {
        id: "generic-listing.quotes.table-header.projectTitle",
        defaultMessage: "Title",
    },
    [QuoteColumnIds.CONTACT]: {
        id: "generic-listing.quotes.table-header.contact",
        defaultMessage: "Contact",
    },
    [QuoteColumnIds.PROPOSED_AT_DATE]: {
        id: "generic-listing.quotes.table-header.proposedDate",
        defaultMessage: "Proposed Date",
    },
    [QuoteColumnIds.CREATED_AT_DATE]: {
        id: "generic-listing.quotes.table-header.createdDate",
        defaultMessage: "Created Date",
    },
    [QuoteColumnIds.STATUS]: {
        id: "generic-listing.quotes.table-header.status",
        defaultMessage: "Status",
    },
    [QuoteColumnIds.TOTAL_VALUE]: {
        id: "generic-listing.quotes.table-header.totalValue",
        defaultMessage: "Value",
    },
    [QuoteColumnIds.ARCHIVED]: {
        id: "generic-listing.quotes.table-header.archived",
        defaultMessage: "Archived",
    },
    [QuoteColumnIds.PO_NUMBER]: {
        id: "generic-listing.quotes.table-header.po-number",
        defaultMessage: "PO Number",
    },
    [QuoteColumnIds.CUSTOMER_TYPE]: {
        id: "generic-listing.quotes.table-header.customer-type",
        defaultMessage: "Customer Type",
    },
    [QuoteColumnIds.DECLINED_REASON]: {
        id: "generic-listing.quotes.table-header.declined-reason",
        defaultMessage: "Declined Reason",
    },
    [QuoteColumnIds.DECLINED_REASON_NOTES]: {
        id: "generic-listing.quotes.table-header.declined-reason-notes",
        defaultMessage: "Declined Reason Notes",
    },
    [QuoteColumnIds.DRAFT_AT_DATE]: {
        id: "generic-listing.quotes.table-header.draftAtDate",
        defaultMessage: "Draft At Date",
    },
    [QuoteColumnIds.CANCELLED_AT_DATE]: {
        id: "generic-listing.quotes.table-header.cancelledAtDate",
        defaultMessage: "Cancelled At Date",
    },
    [QuoteColumnIds.PRE_APPROVED_AT_DATE]: {
        id: "generic-listing.quotes.table-header.preApprovedAtDate",
        defaultMessage: "Pre-approved At Date",
    },
    [QuoteColumnIds.BALANCE]: {
        id: "generic-listing.quotes.table-header.balance",
        defaultMessage: "Balance",
    },
    [QuoteColumnIds.TOTAL_AMOUNT]: {
        id: "generic-listing.quotes.table-header.amount",
        defaultMessage: "Amount",
    },
    [QuoteColumnIds.UPDATED_AT_DATE]: {
        id: "generic-listing.quotes.table-header.updated-at-date",
        defaultMessage: "Updated At Date",
    },
    [QuoteColumnIds.APPROVED_AT_DATE]: {
        id: "generic-listing.quotes.table-header.approved-at-date",
        defaultMessage: "Approved At Date",
    },
    [QuoteColumnIds.DECLINED_AT_DATE]: {
        id: "generic-listing.quotes.table-header.declined-at-date",
        defaultMessage: "Declined At Date",
    },
    [QuoteColumnIds.PRE_DECLINED_AT_DATE]: {
        id: "generic-listing.quotes.table-header.preDeclined-at-date",
        defaultMessage: "Pre-declined At Date",
    },
    [QuoteColumnIds.INVOICED_AT_DATE]: {
        id: "generic-listing.quotes.table-header.invoiced-at-date",
        defaultMessage: "Invoiced At Date",
    },
    [QuoteColumnIds.DEPOSIT_PAID_AT_DATE]: {
        id: "generic-listing.quotes.table-header.deposit-paid-at-date",
        defaultMessage: "Deposit Paid At Date",
    },
    [QuoteColumnIds.EXPIRATION_DATE]: {
        id: "generic-listing.quotes.table-header.expiration-date",
        defaultMessage: "Expiration Date",
    },
    [QuoteColumnIds.ORDERED_AT_DATE]: {
        id: "generic-listing.quotes.table-header.ordered-at-date",
        defaultMessage: "Ordered At Date",
    },
    [QuoteColumnIds.CONTACT_NAME]: {
        id: "generic-listing.quotes.table-header.contact-name",
        defaultMessage: "Contact Name",
    },
    [QuoteColumnIds.CONTACT_FIRST_NAME]: {
        id: "generic-listing.quotes.table-header.contact-first-name",
        defaultMessage: "Contact First Name",
    },
    [QuoteColumnIds.CONTACT_LAST_NAME]: {
        id: "generic-listing.quotes.table-header.contact-last-name",
        defaultMessage: "Contact Last Name",
    },
    [QuoteColumnIds.CONTACT_ORGANIZATION_NAME]: {
        id: "generic-listing.quotes.table-header.contact-organization-name",
        defaultMessage: "Contact Organization Name",
    },
    [QuoteColumnIds.CONTACT_TYPE]: {
        id: "generic-listing.quotes.table-header.contact-type",
        defaultMessage: "Contact Type",
    },
    [QuoteColumnIds.CONTACT_PHONE_NUMBER]: {
        id: "generic-listing.quotes.table-header.contact-phone-number",
        defaultMessage: "Contact Phone Number",
    },
    [QuoteColumnIds.CONTACT_TAX_NUMBER]: {
        id: "generic-listing.quotes.table-header.contact-tax-number",
        defaultMessage: "Contact Tax Number",
    },
    [QuoteColumnIds.CONTACT_EMAIL]: {
        id: "generic-listing.quotes.table-header.contact-email",
        defaultMessage: "Contact Email",
    },
    [QuoteColumnIds.PAID_AMOUNT]: {
        id: "generic-listing.quotes.table-header.paid-amount",
        defaultMessage: "Paid Amount",
    },
    [QuoteColumnIds.CURRENCY]: {
        id: "generic-listing.quotes.table-header.currency",
        defaultMessage: "Currency",
    },
    [QuoteColumnIds.TAX_AMOUNT]: {
        id: "generic-listing.quotes.table-header.total-tax-amount",
        defaultMessage: "Tax Amount",
    },
    [QuoteColumnIds.TAX_ID]: {
        id: "generic-listing.quotes.table-header.tax-id",
        defaultMessage: "Tax ID",
    },
    [QuoteColumnIds.PAYMENT_TRANSACTIONS_TYPE]: {
        id: "generic-listing.quotes.table-header.payment-transactions-type",
        defaultMessage: "Payment Transactions Type",
    },
    [QuoteColumnIds.PAYMENT_TRANSACTIONS_AMOUNT]: {
        id: "generic-listing.quotes.table-header.payment-transactions-amount",
        defaultMessage: "Payment Transactions Amount",
    },
    [QuoteColumnIds.PRODUCTS_TITLE]: {
        id: "generic-listing.quotes.table-header.products-title",
        defaultMessage: "Products Title",
    },
    [QuoteColumnIds.PRODUCTS_SKU]: {
        id: "generic-listing.quotes.table-header.products-sku",
        defaultMessage: "Products SKU",
    },
    [QuoteColumnIds.PRODUCTS_LABELS]: {
        id: "generic-listing.quotes.table-header.products-labels",
        defaultMessage: "Products Labels",
    },
})

const bulkActionDefinitions: BulkActionDefinition[] = [
    {
        id: BULK_OPERATION_TYPE.EXPORT,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.EXPORT],
        icon: "file-export",
        permission: isQuoteBulkExportSupported(),
    },
    {
        id: BULK_OPERATION_TYPE.DELETE,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.DELETE],
        icon: "trash",
        type: BulkActionType.DANGER,
        guard: Permissions.DeleteQuote,
    },
]

const configuration: ObjectTypeConfig = {
    objectType: ObjectType.QUOTES,
    columnDefinitions,
    headerMessages,
    listingPreferencesKey: PREFERENCE_TYPE.LISTING_QUOTES,
    searchPlaceholder: {
        id: "generic-listing.quotes.search-placeholder",
        defaultMessage: "Search a quote...",
    },
    displayString: {
        id: "generic-listing.quotes.display-string",
        defaultMessage: "Quotes",
    },
    searchObjectType: ObjectType.QUOTES,
    bulkActionDefinitions,
    statBoxComponent: require("../../../../common/listing/generic-listing/stat-boxes/QuotesStatBoxes"),
    exportModalComponent: require("../../../../common/listing/generic-listing/export-modals/InvoiceExportModal"),
    creationButtonComponent: require("../../../../common/listing/generic-listing/creation-buttons/QuoteCreationButton"),
}

export default configuration



// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/quotes.ts